import { toPng } from 'html-to-image';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  Aperture,
  BringToFront,
  Camera,
  CheckCircle2,
  Clipboard,
  Download,
  ExternalLink,
  FileJson,
  FolderOpen,
  HeartHandshake,
  Keyboard,
  LocateFixed,
  MessageCircle,
  MousePointer2,
  PenLine,
  Redo2,
  RotateCcw,
  Route,
  Settings2,
  Trash2,
  Undo2,
  Upload,
  X as CloseIcon
} from 'lucide-react';
import {
  buildShortcutHelpGroups,
  createDefaultShortcutMap,
  eventToShortcutCombo,
  findShortcutConflict,
  formatShortcutCombo,
  isEditableShortcutTarget,
  readShortcutMap,
  saveShortcutMap,
  shortcutActionForEvent,
  shortcutGroups,
  type ShortcutAction,
  type ShortcutHelpGroup,
  type ShortcutMap
} from './shortcuts';

type Tool = 'select' | 'character' | 'facing' | 'path' | 'camera';

type Point = {
  x: number;
  y: number;
};

type CharacterMark = Point & {
  id: string;
  name: string;
  color: string;
  labelSize?: number;
  labelWeight?: number;
  facing?: number;
  path: Point[];
  note?: string;
};

type CameraMark = Point & {
  id: string;
  label: string;
  angle: number;
  shotType: string;
  movement: string;
};

type ShotMapState = {
  background: string;
  backgroundName: string;
  shotTitle: string;
  sceneIntent: string;
  characters: CharacterMark[];
  cameras: CameraMark[];
};

type Selection =
  | { kind: 'character'; id: string }
  | { kind: 'camera'; id: string }
  | null;

type DragState = {
  kind: 'character' | 'camera';
  id: string;
  moved: boolean;
};

type KoFiWidget = {
  init: (text: string, color: string, id: string) => void;
  getHTML?: () => string;
};

declare global {
  interface Window {
    kofiwidget2?: KoFiWidget;
  }
}

const STORAGE_KEY = 'shotmap-studio-en-state-v1';
const BUILD_TAG = 'SHOTMAP_EN_20260708_SHORTCUTS_A';
const KO_FI_WIDGET_SCRIPT = 'https://storage.ko-fi.com/cdn/widget/Widget_2.js';
const KO_FI_BUTTON_TEXT = 'Support me on Ko-fi';
const KO_FI_BUTTON_COLOR = '#72a4f2';
const KO_FI_ID = 'W8P32155IN';
const KO_FI_URL = `https://ko-fi.com/${KO_FI_ID}`;
const SAMPLE_SHOT_TITLE = 'Rain Alley Intercept';
const SAMPLE_SHOT_INTENT =
  'The shopkeeper holds under the awning, the woman stops in the middle of the street to negotiate, the man enters from the left street mouth to interrupt, and the lower-right master camera preserves the full street geometry.';

let koFiWidgetPromise: Promise<KoFiWidget> | null = null;

function loadKoFiWidget() {
  if (window.kofiwidget2?.getHTML) {
    return Promise.resolve(window.kofiwidget2);
  }

  if (koFiWidgetPromise) {
    return koFiWidgetPromise;
  }

  koFiWidgetPromise = new Promise((resolve, reject) => {
    const existingScript = document.querySelector<HTMLScriptElement>(`script[src="${KO_FI_WIDGET_SCRIPT}"]`);

    const finish = () => {
      if (window.kofiwidget2) {
        resolve(window.kofiwidget2);
        return;
      }

      reject(new Error('Ko-fi widget did not initialize.'));
    };

    if (existingScript) {
      existingScript.addEventListener('load', finish, { once: true });
      existingScript.addEventListener('error', () => reject(new Error('Ko-fi widget failed to load.')), { once: true });
      window.setTimeout(finish, 0);
      return;
    }

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = KO_FI_WIDGET_SCRIPT;
    script.async = true;
    script.addEventListener('load', finish, { once: true });
    script.addEventListener('error', () => reject(new Error('Ko-fi widget failed to load.')), { once: true });
    document.head.append(script);
  });

  return koFiWidgetPromise;
}

const palette = [
  { name: 'Red', value: '#E84A3A' },
  { name: 'Blue', value: '#2D9CDB' },
  { name: 'Green', value: '#2EC4B6' },
  { name: 'Amber', value: '#DDAA33' },
  { name: 'White', value: '#F3EFE6' },
  { name: 'Black', value: '#111113' },
  { name: 'Orange', value: '#F28C38' },
  { name: 'Cyan', value: '#6FD3CF' }
];

const RECENT_CHARACTER_COLORS_STORAGE_KEY = 'shotmap-studio-en-recent-character-colors-v1';
const MAX_RECENT_CHARACTER_COLORS = 8;
const MIN_LABEL_SIZE = 14;
const MAX_LABEL_SIZE = 42;
const DEFAULT_LABEL_SIZE = 22;
const MIN_LABEL_WEIGHT = 400;
const MAX_LABEL_WEIGHT = 900;
const LABEL_WEIGHT_STEP = 100;
const DEFAULT_LABEL_WEIGHT = 700;

const initialMap: ShotMapState = {
  background: '/sample-overhead-street.jpg',
  backgroundName: 'Original overhead street plate',
  shotTitle: SAMPLE_SHOT_TITLE,
  sceneIntent: SAMPLE_SHOT_INTENT,
  characters: [
    {
      id: 'char-lead',
      name: 'Shopkeeper',
      color: '#E84A3A',
      x: 31.5,
      y: 34.5,
      facing: 94,
      path: [],
      note: 'Holds at the storefront under the awning, facing the woman in the street, with no movement.'
    },
    {
      id: 'char-heroine',
      name: 'Woman',
      color: '#2D9CDB',
      x: 38.2,
      y: 55.2,
      facing: 252,
      path: [],
      note: 'Stops slightly left of center street, looking up toward the shopkeeper as the focus of the exchange.'
    },
    {
      id: 'char-hero',
      name: 'Man',
      color: '#2EC4B6',
      x: 7.8,
      y: 50.5,
      facing: 18,
      path: [
        { x: 15.4, y: 52.4 },
        { x: 24.8, y: 55.1 },
        { x: 33.4, y: 55.7 }
      ],
      note: 'Enters from the left street mouth and follows the dashed path toward the woman to create the interruption beat.'
    }
  ],
  cameras: [
    {
      id: 'cam-1',
      label: 'Master Cam',
      x: 64,
      y: 81,
      angle: 248,
      shotType: 'Overhead wide',
      movement: 'Locked-off',
    }
  ]
};

function readInitialState(): ShotMapState {
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (saved) {
      return JSON.parse(saved) as ShotMapState;
    }
  } catch {
    // Local storage may be unavailable in private contexts.
  }
  return initialMap;
}

function normalizeHexColor(value: string) {
  const compact = value.trim().replace(/^#/, '');
  const expanded =
    compact.length === 3 && /^[0-9a-f]{3}$/i.test(compact)
      ? compact
          .split('')
          .map((part) => part + part)
          .join('')
      : compact;
  return /^[0-9a-f]{6}$/i.test(expanded) ? `#${expanded.toUpperCase()}` : null;
}

function readRecentCharacterColors() {
  try {
    const saved = window.localStorage.getItem(RECENT_CHARACTER_COLORS_STORAGE_KEY);
    if (!saved) return [];
    const parsed = JSON.parse(saved);
    if (!Array.isArray(parsed)) return [];

    return Array.from(
      new Set(
        parsed
          .filter((value): value is string => typeof value === 'string')
          .map(normalizeHexColor)
          .filter((value): value is string => value !== null)
      )
    ).slice(0, MAX_RECENT_CHARACTER_COLORS);
  } catch {
    return [];
  }
}

function cloneState(state: ShotMapState): ShotMapState {
  return JSON.parse(JSON.stringify(state)) as ShotMapState;
}

function makeId(prefix: string) {
  return `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`;
}

function clamp(value: number, min: number, max: number) {
  return Math.max(min, Math.min(max, value));
}

function normalizeNumberControl(value: number | undefined, min: number, max: number, step = 1, fallback = min) {
  const candidate = typeof value === 'number' && Number.isFinite(value) ? value : fallback;
  return Math.round(clamp(candidate, min, max) / step) * step;
}

function normalizeLabelSize(value?: number) {
  return normalizeNumberControl(value, MIN_LABEL_SIZE, MAX_LABEL_SIZE, 1, DEFAULT_LABEL_SIZE);
}

function normalizeLabelWeight(value?: number) {
  return normalizeNumberControl(value, MIN_LABEL_WEIGHT, MAX_LABEL_WEIGHT, LABEL_WEIGHT_STEP, DEFAULT_LABEL_WEIGHT);
}

function angleBetween(a: Point, b: Point) {
  return (Math.atan2(b.y - a.y, b.x - a.x) * 180) / Math.PI;
}

function pointFromEvent(event: React.PointerEvent | React.MouseEvent, element: HTMLElement): Point {
  const rect = element.getBoundingClientRect();
  return {
    x: clamp(((event.clientX - rect.left) / rect.width) * 100, 0, 100),
    y: clamp(((event.clientY - rect.top) / rect.height) * 100, 0, 100)
  };
}

function describeDirection(angle?: number) {
  if (angle === undefined) return 'facing not set';
  const normalized = ((angle % 360) + 360) % 360;
  if (normalized >= 337.5 || normalized < 22.5) return 'facing right';
  if (normalized < 67.5) return 'facing down-right';
  if (normalized < 112.5) return 'facing down';
  if (normalized < 157.5) return 'facing down-left';
  if (normalized < 202.5) return 'facing left';
  if (normalized < 247.5) return 'facing up-left';
  if (normalized < 292.5) return 'facing up';
  return 'facing up-right';
}

function normalizeAngle(angle: number) {
  return ((angle % 360) + 360) % 360;
}

type LabelPlacement = 'top' | 'bottom' | 'left' | 'right';

function labelPlacementForAngle(angle?: number): LabelPlacement {
  if (angle === undefined) return 'top';
  const normalized = normalizeAngle(angle);
  if (normalized >= 315 || normalized < 45) return 'left';
  if (normalized < 135) return 'top';
  if (normalized < 225) return 'right';
  return 'bottom';
}

function sideFromVector(vector: Point): LabelPlacement {
  if (Math.abs(vector.x) >= Math.abs(vector.y)) return vector.x < 0 ? 'left' : 'right';
  return vector.y < 0 ? 'top' : 'bottom';
}

function fallbackPlacement(placement: LabelPlacement, angle?: number): LabelPlacement {
  if (placement === 'left' || placement === 'right') {
    const normalized = angle === undefined ? 90 : normalizeAngle(angle);
    return normalized >= 180 ? 'bottom' : 'top';
  }
  return placement === 'top' ? 'right' : 'left';
}

function labelPlacementForCharacter(character: CharacterMark, map: ShotMapState): LabelPlacement {
  let preferred = labelPlacementForAngle(character.facing);
  if (preferred === 'left' && character.x < 13) preferred = fallbackPlacement(preferred, character.facing);
  if (preferred === 'right' && character.x > 87) preferred = fallbackPlacement(preferred, character.facing);
  if (preferred === 'top' && character.y < 12) preferred = fallbackPlacement(preferred, character.facing);
  if (preferred === 'bottom' && character.y > 88) preferred = fallbackPlacement(preferred, character.facing);

  const hasIncomingPathConflict = map.characters.some((source) => {
    if (source.id === character.id || !source.path.length) return false;
    const terminal = source.path[source.path.length - 1];
    const vector = { x: terminal.x - character.x, y: terminal.y - character.y };
    const distance = Math.hypot(vector.x, vector.y);
    return distance < 9 && sideFromVector(vector) === preferred;
  });
  return hasIncomingPathConflict ? fallbackPlacement(preferred, character.facing) : preferred;
}

function vectorPoint(origin: Point, angle: number, distance: number): Point {
  const radians = (angle * Math.PI) / 180;
  return {
    x: origin.x + Math.cos(radians) * distance,
    y: origin.y + Math.sin(radians) * distance
  };
}

function arrowTipPath(from: Point, to: Point, length = 1.9, width = 0.95) {
  const angle = Math.atan2(to.y - from.y, to.x - from.x);
  const back = {
    x: to.x - Math.cos(angle) * length,
    y: to.y - Math.sin(angle) * length
  };
  const wing = {
    x: Math.cos(angle + Math.PI / 2) * width,
    y: Math.sin(angle + Math.PI / 2) * width
  };
  return `M ${back.x + wing.x} ${back.y + wing.y} L ${to.x} ${to.y} L ${back.x - wing.x} ${back.y - wing.y}`;
}

function buildPrompt(map: ShotMapState) {
  const characterLines = map.characters.map((character) => {
    const pathText =
      character.path.length > 0
        ? `Movement path uses ${character.path.length} nodes and ends near canvas ${character.path.at(-1)?.x.toFixed(1)}% / ${character.path.at(-1)?.y.toFixed(1)}%.`
        : 'Holds current position.';
    const noteText = character.note ? ` Intent: ${character.note}` : '';
    return `- ${character.name}: ${character.color} marker, placed at canvas ${character.x.toFixed(1)}% / ${character.y.toFixed(1)}%, ${describeDirection(character.facing)}. ${pathText}${noteText}`;
  });

  const cameraLines = map.cameras.map((camera) => {
    return `- ${camera.label}: ${camera.shotType}, ${camera.movement}, placed at canvas ${camera.x.toFixed(1)}% / ${camera.y.toFixed(1)}%, aimed at ${Math.round(camera.angle)} degrees.`;
  });

  return [
    'Scene spatial blocking plan:',
    `Scene intent: ${map.sceneIntent || 'Use the marker positions, facing arrows, paths, and camera marks as the scene blocking source of truth.'}`,
    `Background reference: ${map.backgroundName}.`,
    'Characters:',
    characterLines.length ? characterLines.join('\n') : '- No characters placed yet.',
    'Camera:',
    cameraLines.length ? cameraLines.join('\n') : '- No camera marked yet.',
    'Generation constraint:',
    'Preserve relative positions, facing directions, and movement paths. Treat the map as the source of truth for character blocking before generating the video.',
    `Build tag: ${BUILD_TAG}`
  ].join('\n');
}

async function loadImage(src: string): Promise<HTMLImageElement> {
  const image = new Image();
  image.crossOrigin = 'anonymous';
  image.src = src;
  await image.decode();
  return image;
}

function drawArrowHead(
  context: CanvasRenderingContext2D,
  fromX: number,
  fromY: number,
  toX: number,
  toY: number,
  color: string,
  size = 18
) {
  const angle = Math.atan2(toY - fromY, toX - fromX);
  context.save();
  context.translate(toX, toY);
  context.rotate(angle);
  context.fillStyle = color;
  context.beginPath();
  context.moveTo(0, 0);
  context.lineTo(-size, size * 0.52);
  context.lineTo(-size, -size * 0.52);
  context.closePath();
  context.fill();
  context.restore();
}

function roundedRectPath(context: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, radius: number) {
  const corner = Math.min(radius, width / 2, height / 2);
  context.beginPath();
  context.moveTo(x + corner, y);
  context.lineTo(x + width - corner, y);
  context.quadraticCurveTo(x + width, y, x + width, y + corner);
  context.lineTo(x + width, y + height - corner);
  context.quadraticCurveTo(x + width, y + height, x + width - corner, y + height);
  context.lineTo(x + corner, y + height);
  context.quadraticCurveTo(x, y + height, x, y + height - corner);
  context.lineTo(x, y + corner);
  context.quadraticCurveTo(x, y, x + corner, y);
  context.closePath();
}

function drawFloatingExportLegend(context: CanvasRenderingContext2D, width: number, height: number, map: ShotMapState) {
  const scale = width / 1664;
  const margin = Math.max(22, width * 0.022);
  const panelWidth = Math.min(width - margin * 2, width < 980 ? width * 0.82 : Math.min(420, Math.max(390, width * 0.28)));
  const padding = Math.max(16, Math.min(24, width * 0.014));
  const titleSize = Math.max(19, Math.min(25, width * 0.016));
  const labelSize = Math.max(14, Math.min(18, width * 0.0115));
  const detailSize = Math.max(12, Math.min(15, width * 0.009));
  const iconScale = Math.max(0.78, Math.min(1.06, scale));
  const items: { kind: 'dot' | 'facing' | 'dashed' | 'camera'; label: string; detail: string }[] = [];
  if (map.characters.length > 0) items.push({ kind: 'dot', label: 'Character Dot', detail: 'Current position' });
  if (map.characters.some((character) => character.facing !== undefined)) {
    items.push({ kind: 'facing', label: 'Facing Tip', detail: 'Facing direction' });
  }
  if (map.characters.some((character) => character.path.length > 0)) {
    items.push({ kind: 'dashed', label: 'Dashed Arrow', detail: 'Movement path and direction' });
  }
  if (map.cameras.length > 0) items.push({ kind: 'camera', label: 'Amber Camera', detail: 'Camera position and aim' });
  if (!items.length) return;

  const columns = width < 980 ? 1 : Math.min(2, items.length);
  const rows = Math.max(1, Math.ceil(items.length / columns));
  const rowHeight = Math.max(46, Math.min(58, width * 0.034));
  const headerHeight = titleSize + detailSize + Math.max(18, padding * 0.82);
  const panelHeight = padding * 2 + headerHeight + rows * rowHeight;
  const panelX = width - margin - panelWidth;
  const panelY = height - margin - panelHeight;
  const cellWidth = (panelWidth - padding * 2) / columns;
  const itemStartY = panelY + padding + headerHeight;

  context.save();
  context.shadowColor = 'rgba(0, 0, 0, 0.48)';
  context.shadowBlur = Math.max(18, width * 0.018);
  context.shadowOffsetY = Math.max(8, width * 0.006);
  roundedRectPath(context, panelX, panelY, panelWidth, panelHeight, Math.max(14, width * 0.009));
  context.fillStyle = 'rgba(17, 17, 19, 0.9)';
  context.fill();
  context.shadowColor = 'transparent';
  context.strokeStyle = 'rgba(221, 170, 51, 0.64)';
  context.lineWidth = Math.max(2, width * 0.0012);
  roundedRectPath(context, panelX, panelY, panelWidth, panelHeight, Math.max(14, width * 0.009));
  context.stroke();
  context.beginPath();
  context.moveTo(panelX + padding, panelY + padding + titleSize + 7);
  context.lineTo(panelX + panelWidth - padding, panelY + padding + titleSize + 7);
  context.strokeStyle = 'rgba(243, 239, 230, 0.12)';
  context.lineWidth = Math.max(1, width * 0.0008);
  context.stroke();

  context.textAlign = 'left';
  context.textBaseline = 'alphabetic';
  context.font = `800 ${titleSize}px "Archivo", "Manrope", sans-serif`;
  context.fillStyle = '#DDAA33';
  context.fillText('LEGEND', panelX + padding, panelY + padding + titleSize);
  context.font = `700 ${detailSize}px "Manrope", sans-serif`;
  context.fillStyle = 'rgba(243, 239, 230, 0.78)';
  context.fillText('Only marks visible in this map', panelX + padding, panelY + padding + titleSize + detailSize + 22);

  items.forEach((item, index) => {
    const column = index % columns;
    const row = Math.floor(index / columns);
    const cellX = panelX + padding + column * cellWidth;
    const cellY = itemStartY + row * rowHeight;
    const iconX = cellX + Math.max(16, 20 * iconScale);
    const iconY = cellY + rowHeight * 0.52;
    const textX = cellX + Math.max(42, 48 * iconScale);
    const textMaxWidth = Math.max(80, cellWidth - (textX - cellX) - 8);

    context.save();
    if (item.kind === 'dot') {
      context.beginPath();
      context.arc(iconX, iconY, 10 * iconScale, 0, Math.PI * 2);
      context.fillStyle = '#E84A3A';
      context.fill();
      context.lineWidth = 3.5 * iconScale;
      context.strokeStyle = '#F3EFE6';
      context.stroke();
    }
    if (item.kind === 'facing') {
      drawArrowHead(context, iconX - 10 * iconScale, iconY, iconX + 10 * iconScale, iconY, '#2EC4B6', 11 * iconScale);
    }
    if (item.kind === 'dashed') {
      context.strokeStyle = '#2EC4B6';
      context.lineWidth = 3.5 * iconScale;
      context.lineCap = 'butt';
      context.setLineDash([8 * iconScale, 6 * iconScale]);
      context.beginPath();
      context.moveTo(iconX - 17 * iconScale, iconY);
      context.lineTo(iconX + 17 * iconScale, iconY);
      context.stroke();
      context.setLineDash([]);
      drawArrowHead(context, iconX - 17 * iconScale, iconY, iconX + 24 * iconScale, iconY, '#2EC4B6', 10 * iconScale);
    }
    if (item.kind === 'camera') {
      context.fillStyle = 'rgba(17, 17, 19, 0.9)';
      context.strokeStyle = '#DDAA33';
      context.lineWidth = 3 * iconScale;
      context.beginPath();
      context.arc(iconX - 5 * iconScale, iconY, 9.5 * iconScale, 0, Math.PI * 2);
      context.fill();
      context.stroke();
      drawArrowHead(context, iconX + 1 * iconScale, iconY + 3 * iconScale, iconX + 15 * iconScale, iconY - 7 * iconScale, '#DDAA33', 8.5 * iconScale);
    }
    context.restore();

    context.fillStyle = '#F3EFE6';
    context.font = `800 ${labelSize}px "Archivo", "Manrope", sans-serif`;
    context.fillText(item.label, textX, cellY + rowHeight * 0.42, textMaxWidth);
    context.fillStyle = 'rgba(243, 239, 230, 0.72)';
    context.font = `700 ${detailSize}px "Manrope", sans-serif`;
    context.fillText(item.detail, textX, cellY + rowHeight * 0.76, textMaxWidth);
  });
  context.restore();
}

async function exportPng(map: ShotMapState, stageElement: HTMLElement | null) {
  if (!stageElement) return;
  stageElement.classList.add('exporting');
  let stageDataUrl = '';
  try {
    await new Promise<void>((resolve) => {
      window.requestAnimationFrame(() => resolve());
    });
    stageDataUrl = await toPng(stageElement, {
      backgroundColor: '#0b0b0d',
      cacheBust: true,
      pixelRatio: Math.max(2, Math.min(3, window.devicePixelRatio || 2))
    });
  } finally {
    stageElement.classList.remove('exporting');
  }

  const image = await loadImage(stageDataUrl);
  const canvas = document.createElement('canvas');
  const imageWidth = image.naturalWidth || stageElement.offsetWidth || 1664;
  const imageHeight = image.naturalHeight || stageElement.offsetHeight || 1080;
  canvas.width = imageWidth;
  canvas.height = imageHeight;
  const context = canvas.getContext('2d');
  if (!context) return;

  context.drawImage(image, 0, 0, imageWidth, imageHeight);
  drawFloatingExportLegend(context, canvas.width, canvas.height, map);

  const link = document.createElement('a');
  link.download = `shotmap-${Date.now()}.png`;
  link.href = canvas.toDataURL('image/png');
  link.click();
}

function KoFiSupportButton({
  className = '',
  fallbackClassName = 'toolbar-button'
}: {
  className?: string;
  fallbackClassName?: string;
}) {
  const [widgetHTML, setWidgetHTML] = useState('');

  useEffect(() => {
    let cancelled = false;

    loadKoFiWidget()
      .then((widget) => {
        widget.init(KO_FI_BUTTON_TEXT, KO_FI_BUTTON_COLOR, KO_FI_ID);
        const html = widget.getHTML?.() ?? '';

        if (!cancelled) {
          setWidgetHTML(html);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setWidgetHTML('');
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  if (widgetHTML) {
    return <span className={`kofi-widget-slot ${className}`} dangerouslySetInnerHTML={{ __html: widgetHTML }} />;
  }

  return (
    <a className={fallbackClassName} href={KO_FI_URL} target="_blank" rel="noopener noreferrer" title={KO_FI_BUTTON_TEXT}>
      <HeartHandshake size={18} />
      <span>{KO_FI_BUTTON_TEXT}</span>
    </a>
  );
}

function SupportPage() {
  useEffect(() => {
    document.title = 'Support Creator - ShotMap Studio';
  }, []);

  return (
    <main className="support-shell">
      <header className="support-topbar">
        <a className="support-brand" href="/">
          <span className="brand-kicker">AI video blocking tool</span>
          <strong>ShotMap Studio</strong>
        </a>
        <nav className="support-nav" aria-label="Support page navigation">
          <a className="toolbar-button" href="/">
            <MousePointer2 size={18} />
            <span>Back to Studio</span>
          </a>
          <a className="toolbar-button active" href="/support" aria-current="page">
            <HeartHandshake size={18} />
            <span>Support Creator</span>
          </a>
        </nav>
      </header>

      <section className="support-page">
        <div className="support-hero">
          <div className="support-copy">
            <span className="brand-kicker">Support the maker</span>
            <h1>Help ShotMap Studio keep getting sharper</h1>
            <p>
              If this blocking planner saves you time while briefing AI video shots, organizing prompts, or explaining camera coverage, your support helps me keep refining the tool.
            </p>
          </div>
          <div className="support-note">
            <HeartHandshake size={22} />
            <p>Support is voluntary. It does not unlock paid-only features or create a commercial commitment. The current tool remains free to use.</p>
          </div>
        </div>

        <section className="support-methods support-methods-kofi" aria-label="Support methods">
          <article className="support-method-card support-platform-card support-kofi-card">
            <div className="support-method-head">
              <HeartHandshake size={18} />
              <div>
                <strong>Support me on Ko-fi</strong>
                <span>One-time support through the official Ko-fi widget</span>
              </div>
            </div>
            <p>
              Ko-fi is the preferred support route for the English version. It keeps support simple, global, and separate from the free tool experience.
            </p>
            <KoFiSupportButton className="support-kofi-widget" fallbackClassName="support-primary-link" />
          </article>

          <article className="support-method-card">
            <div className="support-method-head">
              <ExternalLink size={18} />
              <div>
                <strong>What support helps with</strong>
                <span>Small improvements compound quickly</span>
              </div>
            </div>
            <p>
              Your support helps cover hosting, browser testing, export polish, new prompt formats, and the small interface details that make ShotMap Studio easier to use in real AI video workflows.
            </p>
          </article>
        </section>

        <section className="support-contact" aria-label="Contact creator">
          <div className="support-contact-copy">
            <MessageCircle size={22} />
            <span className="brand-kicker">Contact</span>
            <h2>Contact the creator</h2>
            <p>
              For bug reports, AI video workflow discussions, feature requests, or collaboration ideas, scan the QR code to add me on WeChat. A short note mentioning ShotMap Studio helps.
            </p>
          </div>
          <div className="support-contact-qr">
            <img src="/support/wechat-contact.jpg" alt="Creator WeChat contact QR code" loading="lazy" decoding="async" />
          </div>
        </section>
      </section>
    </main>
  );
}

function ShortcutKey({ children }: { children: string }) {
  return <kbd className="shortcut-key">{children}</kbd>;
}

type ShortcutHelpPanelProps = {
  groups: ShortcutHelpGroup[];
  onOpenSettings: () => void;
};

function ShortcutHelpPanel({ groups, onOpenSettings }: ShortcutHelpPanelProps) {
  return (
    <div className="shortcut-help">
      <div className="panel-title shortcut-help-title">
        <span>
          <Keyboard size={16} />
          <span>Shortcuts</span>
        </span>
        <button className="mini-icon-button" onClick={onOpenSettings} title="Shortcut settings" aria-label="Shortcut settings">
          <Settings2 size={15} />
        </button>
      </div>
      <div className="shortcut-help-list">
        {groups.map((group) => (
          <div className="shortcut-help-group" key={group.title}>
            <span className="section-label">{group.title}</span>
            {group.items.map((item) => (
              <div className="shortcut-help-row" key={`${group.title}-${item.label}`}>
                <span className="shortcut-help-label">
                  {item.label}
                  {item.detail && <small>{item.detail}</small>}
                </span>
                <span className="shortcut-keyset">
                  {item.keys.map((key) => (
                    <ShortcutKey key={key}>{key}</ShortcutKey>
                  ))}
                </span>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

type ShortcutSettingsDialogProps = {
  shortcuts: ShortcutMap;
  recordingAction: ShortcutAction | null;
  conflictMessage: string;
  onClose: () => void;
  onStartRecording: (action: ShortcutAction) => void;
  onResetAction: (action: ShortcutAction) => void;
  onResetAll: () => void;
};

function ShortcutSettingsDialog({
  shortcuts,
  recordingAction,
  conflictMessage,
  onClose,
  onStartRecording,
  onResetAction,
  onResetAll
}: ShortcutSettingsDialogProps) {
  return (
    <div className="shortcut-modal-backdrop" role="presentation">
      <section className="shortcut-modal" role="dialog" aria-modal="true" aria-labelledby="shortcut-settings-title">
        <header className="shortcut-modal-head">
          <div>
            <span className="brand-kicker">Keyboard Control</span>
            <h2 id="shortcut-settings-title">Shortcut Settings</h2>
          </div>
          <button className="icon-button" onClick={onClose} title="Close shortcut settings" aria-label="Close shortcut settings">
            <CloseIcon size={18} />
          </button>
        </header>

        {conflictMessage && (
          <div className="shortcut-conflict" role="alert">
            <AlertTriangle size={16} />
            <span>{conflictMessage}</span>
          </div>
        )}

        <div className="shortcut-settings-list">
          {shortcutGroups.map((group) => (
            <div className="shortcut-settings-group" key={group.title}>
              <span className="section-label">{group.title}</span>
              {group.definitions.map((definition) => {
                const combos = shortcuts[definition.action] ?? [];
                const isRecording = recordingAction === definition.action;
                return (
                  <div className={isRecording ? 'shortcut-setting-row recording' : 'shortcut-setting-row'} key={definition.action}>
                    <div className="shortcut-setting-copy">
                      <strong>{definition.label}</strong>
                      {definition.detail && <span>{definition.detail}</span>}
                    </div>
                    <div className="shortcut-setting-keys">
                      {isRecording ? (
                        <span className="recording-pill">Press a new shortcut</span>
                      ) : (
                        combos.map((combo) => <ShortcutKey key={combo}>{formatShortcutCombo(combo)}</ShortcutKey>)
                      )}
                    </div>
                    <div className="shortcut-setting-actions">
                      <button className="ghost-action" onClick={() => onStartRecording(definition.action)}>
                        {isRecording ? 'Recording' : 'Record'}
                      </button>
                      <button className="mini-icon-button" onClick={() => onResetAction(definition.action)} title="Restore this shortcut" aria-label={`Restore ${definition.label} shortcut`}>
                        <RotateCcw size={14} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        <footer className="shortcut-modal-foot">
          <p>Press Esc while recording to cancel. If a shortcut is already assigned, ShotMap Studio will keep the existing setting and show the conflict.</p>
          <button className="ghost-action" onClick={onResetAll}>
            <RotateCcw size={15} />
            Restore Defaults
          </button>
        </footer>
      </section>
    </div>
  );
}

type CharacterColorPickerProps = {
  value: string;
  recentColors: string[];
  onChange: (color: string) => void;
  onCustomColor: (color: string) => void;
};

function CharacterColorPicker({ value, recentColors, onChange, onCustomColor }: CharacterColorPickerProps) {
  const resolvedColor = normalizeHexColor(value) ?? palette[0].value;
  const [hexDraft, setHexDraft] = useState(resolvedColor);

  useEffect(() => {
    setHexDraft(resolvedColor);
  }, [resolvedColor]);

  const applyCustomColor = (candidate: string) => {
    const normalized = normalizeHexColor(candidate);
    if (!normalized) return false;
    onChange(normalized);
    onCustomColor(normalized);
    setHexDraft(normalized);
    return true;
  };

  return (
    <div className="character-color-picker">
      <div className="color-picker-section">
        <span className="color-picker-section-label">Presets</span>
        <div className="swatches">
          {palette.map((item) => (
            <button
              key={item.value}
              type="button"
              className={resolvedColor === item.value ? 'swatch selected' : 'swatch'}
              style={{ '--swatch-color': item.value } as React.CSSProperties}
              onClick={() => onChange(item.value)}
              aria-label={`Choose ${item.name}`}
              title={item.name}
            >
              <span className="swatch-chip" aria-hidden="true" />
              <span className="swatch-name">{item.name}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="color-picker-section">
        <span className="color-picker-section-label">Custom color</span>
        <div className="color-picker-custom-row">
          <label className="native-color-field" title="Open the system color picker">
            <input
              type="color"
              value={resolvedColor}
              onChange={(event) => applyCustomColor(event.target.value)}
              aria-label="Choose a custom character color"
            />
            <span>Pick</span>
          </label>
          <label className="hex-color-field">
            <span>HEX</span>
            <input
              value={hexDraft}
              onChange={(event) => {
                const next = event.target.value.toUpperCase();
                setHexDraft(next);
                applyCustomColor(next);
              }}
              onBlur={() => setHexDraft(resolvedColor)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') event.currentTarget.blur();
              }}
              inputMode="text"
              maxLength={7}
              spellCheck={false}
              aria-label="Enter a hexadecimal character color"
            />
          </label>
        </div>
      </div>

      {recentColors.length > 0 && (
        <div className="color-picker-section recent-color-section">
          <span className="color-picker-section-label">Recently used</span>
          <div className="recent-color-swatches">
            {recentColors.map((color) => (
              <button
                key={color}
                type="button"
                className={resolvedColor === color ? 'recent-color-swatch selected' : 'recent-color-swatch'}
                style={{ '--swatch-color': color } as React.CSSProperties}
                onClick={() => onChange(color)}
                aria-label={`Use recent color ${color}`}
                title={color}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

type RangeNumberControlProps = {
  label: string;
  description: string;
  hint: string;
  min: number;
  max: number;
  step: number;
  value: number;
  unit?: string;
  onChange: (value: number) => void;
};

function RangeNumberControl({ label, description, hint, min, max, step, value, unit, onChange }: RangeNumberControlProps) {
  const resolvedValue = normalizeNumberControl(value, min, max, step, min);
  const [draftValue, setDraftValue] = useState(String(resolvedValue));

  useEffect(() => {
    setDraftValue(String(resolvedValue));
  }, [resolvedValue]);

  const commitDraftValue = () => {
    const candidate = Number(draftValue);
    const nextValue = Number.isFinite(candidate) ? normalizeNumberControl(candidate, min, max, step, resolvedValue) : resolvedValue;
    setDraftValue(String(nextValue));
    if (nextValue !== resolvedValue) onChange(nextValue);
  };

  return (
    <div className="range-number-control">
      <div className="range-number-copy">
        <span>{label}</span>
        <small>{hint}</small>
      </div>
      <p className="range-number-description">{description}</p>
      <div className="range-number-inputs">
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={resolvedValue}
          onChange={(event) => onChange(normalizeNumberControl(Number(event.target.value), min, max, step, resolvedValue))}
          aria-label={`Adjust ${label}`}
        />
        <label className="range-number-field">
          <input
            type="number"
            min={min}
            max={max}
            step={step}
            value={draftValue}
            onChange={(event) => setDraftValue(event.target.value)}
            onBlur={commitDraftValue}
            onKeyDown={(event) => {
              if (event.key === 'Enter') event.currentTarget.blur();
            }}
            inputMode="numeric"
            aria-label={`Enter ${label}`}
          />
          {unit && <span>{unit}</span>}
        </label>
      </div>
    </div>
  );
}

type CharacterLabelTypographyControlsProps = {
  size: number;
  weight: number;
  onSizeChange: (value: number) => void;
  onWeightChange: (value: number) => void;
};

function CharacterLabelTypographyControls({ size, weight, onSizeChange, onWeightChange }: CharacterLabelTypographyControlsProps) {
  return (
    <div className="character-label-typography">
      <RangeNumberControl
        label="Name size"
        description="Higher values make the label more visible."
        hint={`Small to large | ${MIN_LABEL_SIZE}-${MAX_LABEL_SIZE} px`}
        min={MIN_LABEL_SIZE}
        max={MAX_LABEL_SIZE}
        step={1}
        value={size}
        unit="px"
        onChange={onSizeChange}
      />
      <RangeNumberControl
        label="Name boldness"
        description="Higher values make the letters bolder."
        hint={`Light to bold | ${MIN_LABEL_WEIGHT}-${MAX_LABEL_WEIGHT}`}
        min={MIN_LABEL_WEIGHT}
        max={MAX_LABEL_WEIGHT}
        step={LABEL_WEIGHT_STEP}
        value={weight}
        onChange={onWeightChange}
      />
    </div>
  );
}

function App() {
  const [map, setMap] = useState<ShotMapState>(() => readInitialState());
  const [tool, setTool] = useState<Tool>('select');
  const [selection, setSelection] = useState<Selection>(null);
  const [draftName, setDraftName] = useState('Character');
  const [draftColor, setDraftColor] = useState(palette[0].value);
  const [draftLabelSize, setDraftLabelSize] = useState(DEFAULT_LABEL_SIZE);
  const [draftLabelWeight, setDraftLabelWeight] = useState(DEFAULT_LABEL_WEIGHT);
  const [recentCharacterColors, setRecentCharacterColors] = useState<string[]>(() => readRecentCharacterColors());
  const [focusDraftName, setFocusDraftName] = useState(false);
  const [shortcuts, setShortcuts] = useState<ShortcutMap>(() => readShortcutMap());
  const [shortcutSettingsOpen, setShortcutSettingsOpen] = useState(false);
  const [recordingAction, setRecordingAction] = useState<ShortcutAction | null>(null);
  const [shortcutConflict, setShortcutConflict] = useState('');
  const [status, setStatus] = useState('Current sample: the shopkeeper holds at the doorway, the woman anchors the street center, the man enters from the left, and the lower-right camera covers the overhead wide.');
  const [toast, setToast] = useState<{ id: number; message: string } | null>(null);
  const [past, setPast] = useState<ShotMapState[]>([]);
  const [future, setFuture] = useState<ShotMapState[]>([]);
  const [dragging, setDragging] = useState<DragState | null>(null);
  const stageRef = useRef<HTMLDivElement | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const dragSnapshotRef = useRef<ShotMapState | null>(null);
  const toastIdRef = useRef(0);
  const toastTimerRef = useRef<number | null>(null);

  const selectedCharacter = useMemo(() => {
    if (selection?.kind !== 'character') return null;
    return map.characters.find((character) => character.id === selection.id) ?? null;
  }, [map.characters, selection]);

  const selectedCamera = useMemo(() => {
    if (selection?.kind !== 'camera') return null;
    return map.cameras.find((camera) => camera.id === selection.id) ?? null;
  }, [map.cameras, selection]);

  const prompt = useMemo(() => buildPrompt(map), [map]);
  const visibleShortcutGroups = useMemo(() => buildShortcutHelpGroups(shortcuts), [shortcuts]);

  const showStatus = (message: string) => {
    setStatus(message);
    const id = toastIdRef.current + 1;
    toastIdRef.current = id;
    if (toastTimerRef.current) {
      window.clearTimeout(toastTimerRef.current);
    }
    setToast({ id, message });
    toastTimerRef.current = window.setTimeout(() => {
      setToast((current) => (current?.id === id ? null : current));
    }, 3600);
  };

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
    };
  }, []);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
    } catch {
      showStatus('Browser storage is full. Your current edit can still be exported.');
    }
  }, [map]);

  useEffect(() => {
    try {
      window.localStorage.setItem(RECENT_CHARACTER_COLORS_STORAGE_KEY, JSON.stringify(recentCharacterColors));
    } catch {
      showStatus('Recently used colors could not be saved. They still work in this page session.');
    }
  }, [recentCharacterColors]);

  useEffect(() => {
    try {
      saveShortcutMap(shortcuts);
    } catch {
      showStatus('Shortcut settings could not be saved. They still work in this page session.');
    }
  }, [shortcuts]);

  useEffect(() => {
    const handlePaste = (event: ClipboardEvent) => {
      const item = Array.from(event.clipboardData?.items ?? []).find((entry) => entry.type.startsWith('image/'));
      const file = item?.getAsFile();
      if (file) {
        void loadBackgroundFile(file);
      }
    };
    window.addEventListener('paste', handlePaste);
    return () => window.removeEventListener('paste', handlePaste);
  }, []);

  const commit = (next: ShotMapState, message?: string) => {
    setPast((items) => [...items.slice(-49), cloneState(map)]);
    setFuture([]);
    setMap(next);
    if (message) showStatus(message);
  };

  const updateCharacter = (id: string, patch: Partial<CharacterMark>, message?: string) => {
    commit(
      {
        ...map,
        characters: map.characters.map((character) => (character.id === id ? { ...character, ...patch } : character))
      },
      message
    );
  };

  const rememberRecentCharacterColor = (color: string) => {
    const normalized = normalizeHexColor(color);
    if (!normalized) return;
    setRecentCharacterColors((current) => [normalized, ...current.filter((item) => item !== normalized)].slice(0, MAX_RECENT_CHARACTER_COLORS));
  };

  const updateSelectedCharacterColor = (color: string) => {
    if (!selectedCharacter) return;
    updateCharacter(selectedCharacter.id, { color });
  };

  const updateCamera = (id: string, patch: Partial<CameraMark>, message?: string) => {
    commit(
      {
        ...map,
        cameras: map.cameras.map((camera) => (camera.id === id ? { ...camera, ...patch } : camera))
      },
      message
    );
  };

  const undo = () => {
    if (!past.length) return;
    const previous = past[past.length - 1];
    setPast((items) => items.slice(0, -1));
    setFuture((items) => [cloneState(map), ...items.slice(0, 49)]);
    setMap(previous);
    showStatus('Undid one step.');
  };

  const redo = () => {
    if (!future.length) return;
    const next = future[0];
    setFuture((items) => items.slice(1));
    setPast((items) => [...items.slice(-49), cloneState(map)]);
    setMap(next);
    showStatus('Redid one step.');
  };

  const loadBackgroundFile = async (file: File) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result !== 'string') return;
      commit(
        {
          ...map,
          background: reader.result,
          backgroundName: file.name,
          shotTitle: 'Custom Blocking Map',
          sceneIntent: 'User-uploaded plate. Treat the character positions, facing arrows, movement paths, and camera marks as the source of truth for scene blocking.'
        },
        `Loaded background: ${file.name}`
      );
    };
    reader.readAsDataURL(file);
  };

  const handleStageClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!stageRef.current || dragging?.moved) return;
    const target = event.target instanceof HTMLElement ? event.target : null;
    if (target?.closest('.character-pin, .camera-pin')) return;
    const point = pointFromEvent(event, stageRef.current);
    if (tool === 'character') {
      const name = draftName.trim() || `Character ${map.characters.length + 1}`;
      const character: CharacterMark = {
        id: makeId('char'),
        name,
        color: draftColor,
        labelSize: draftLabelSize,
        labelWeight: draftLabelWeight,
        x: point.x,
        y: point.y,
        facing: undefined,
        path: []
      };
      commit(
        {
          ...map,
          characters: [...map.characters, character]
        },
        `Added character: ${name}. Switched back to Select so you can edit it now.`
      );
      setSelection({ kind: 'character', id: character.id });
      setTool('select');
      setDraftName(`Character ${map.characters.length + 2}`);
      return;
    }

    if (tool === 'camera') {
      const camera: CameraMark = {
        id: makeId('cam'),
        label: `Camera ${map.cameras.length + 1}`,
        x: point.x,
        y: point.y,
        angle: 270,
        shotType: 'Medium shot',
        movement: 'Locked-off'
      };
      commit(
        {
          ...map,
          cameras: [...map.cameras, camera]
        },
        `Added camera: ${camera.label}`
      );
      setSelection({ kind: 'camera', id: camera.id });
      return;
    }

    if (tool === 'facing' && selectedCharacter) {
      updateCharacter(selectedCharacter.id, { facing: angleBetween(selectedCharacter, point) }, `Set facing direction for ${selectedCharacter.name}.`);
      return;
    }

    if (tool === 'path' && selectedCharacter) {
      updateCharacter(
        selectedCharacter.id,
        { path: [...selectedCharacter.path, point] },
        `Added a movement node for ${selectedCharacter.name}.`
      );
      return;
    }

    if (tool === 'path' || tool === 'facing') {
      showStatus('Select a character before setting facing or movement path.');
      return;
    }

    setSelection(null);
  };

  const startDrag = (event: React.PointerEvent, kind: 'character' | 'camera', id: string) => {
    event.stopPropagation();
    setSelection({ kind, id } as Selection);
    if (tool !== 'select') return;
    dragSnapshotRef.current = cloneState(map);
    setDragging({ kind, id, moved: false });
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!dragging || !stageRef.current) return;
    const point = pointFromEvent(event, stageRef.current);
    setDragging((current) => (current ? { ...current, moved: true } : current));
    setMap((current) => {
      if (dragging.kind === 'character') {
        return {
          ...current,
          characters: current.characters.map((character) =>
            character.id === dragging.id ? { ...character, x: point.x, y: point.y } : character
          )
        };
      }
      return {
        ...current,
        cameras: current.cameras.map((camera) => (camera.id === dragging.id ? { ...camera, x: point.x, y: point.y } : camera))
      };
    });
  };

  const finishDrag = () => {
    if (dragging?.moved && dragSnapshotRef.current) {
      setPast((items) => [...items.slice(-49), dragSnapshotRef.current as ShotMapState]);
      setFuture([]);
      showStatus('Moved object.');
    }
    dragSnapshotRef.current = null;
    setDragging(null);
  };

  const deleteSelected = () => {
    if (!selection) return;
    if (selection.kind === 'character') {
      commit(
        {
          ...map,
          characters: map.characters.filter((character) => character.id !== selection.id)
        },
        'Deleted character.'
      );
    } else {
      commit(
        {
          ...map,
          cameras: map.cameras.filter((camera) => camera.id !== selection.id)
        },
        'Deleted camera.'
      );
    }
    setSelection(null);
  };

  const clearSelectedPath = () => {
    if (!selectedCharacter) return;
    updateCharacter(selectedCharacter.id, { path: [] }, `Cleared movement path for ${selectedCharacter.name}.`);
  };

  const moveSelectedBy = (deltaX: number, deltaY: number) => {
    if (selectedCharacter) {
      updateCharacter(
        selectedCharacter.id,
        {
          x: clamp(selectedCharacter.x + deltaX, 0, 100),
          y: clamp(selectedCharacter.y + deltaY, 0, 100)
        },
        `Adjusted position for ${selectedCharacter.name}.`
      );
      return;
    }
    if (selectedCamera) {
      updateCamera(
        selectedCamera.id,
        {
          x: clamp(selectedCamera.x + deltaX, 0, 100),
          y: clamp(selectedCamera.y + deltaY, 0, 100)
        },
        `Adjusted position for ${selectedCamera.label}.`
      );
      return;
    }
    showStatus('Select a character or camera before using arrow-key nudging.');
  };

  const copyText = async (text: string, message: string) => {
    await navigator.clipboard.writeText(text);
    showStatus(message);
  };

  const handleExport = async () => {
    showStatus('Capturing the current map and adding the floating legend.');
    try {
      await exportPng(map, stageRef.current);
      showStatus('Exported PNG with the bottom-right legend overlay.');
    } catch (error) {
      console.error(error);
      showStatus('Export failed. Please retry or refresh the page.');
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const file = Array.from(event.dataTransfer.files).find((entry) => entry.type.startsWith('image/'));
    if (file) void loadBackgroundFile(file);
  };

  const startShortcutRecording = (action: ShortcutAction) => {
    setRecordingAction(action);
    setShortcutConflict('');
  };

  const resetShortcutAction = (action: ShortcutAction) => {
    const defaults = createDefaultShortcutMap();
    setShortcuts((current) => ({
      ...current,
      [action]: [...defaults[action]]
    }));
    setRecordingAction(null);
    setShortcutConflict('');
    showStatus('Restored the default shortcut for this action.');
  };

  const resetAllShortcuts = () => {
    setShortcuts(createDefaultShortcutMap());
    setRecordingAction(null);
    setShortcutConflict('');
    showStatus('Restored all default shortcuts.');
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (shortcutSettingsOpen) {
        if (recordingAction) {
          event.preventDefault();
          if (event.key === 'Escape') {
            setRecordingAction(null);
            setShortcutConflict('');
            return;
          }
          const combo = eventToShortcutCombo(event);
          if (!combo) return;
          const conflict = findShortcutConflict(shortcuts, recordingAction, combo);
          if (conflict) {
            setShortcutConflict(`${formatShortcutCombo(combo)} is already assigned to ${conflict.label}. Choose another shortcut.`);
            return;
          }
          setShortcuts((current) => ({
            ...current,
            [recordingAction]: [combo]
          }));
          setRecordingAction(null);
          setShortcutConflict('');
          showStatus(`Shortcut set to ${formatShortcutCombo(combo)}.`);
          return;
        }
        if (event.key === 'Escape') {
          event.preventDefault();
          setShortcutSettingsOpen(false);
          setShortcutConflict('');
        }
        return;
      }

      if (isEditableShortcutTarget(event.target) || dragging) return;

      if (event.key.startsWith('Arrow')) {
        event.preventDefault();
        const step = event.altKey ? 0.08 : event.shiftKey ? 1.2 : 0.35;
        const delta = {
          ArrowUp: { x: 0, y: -step },
          ArrowDown: { x: 0, y: step },
          ArrowLeft: { x: -step, y: 0 },
          ArrowRight: { x: step, y: 0 }
        }[event.key];
        if (delta) moveSelectedBy(delta.x, delta.y);
        return;
      }

      const action = shortcutActionForEvent(event, shortcuts);
      if (!action) return;
      event.preventDefault();

      if (action === 'tool.select') {
        setTool('select');
        setFocusDraftName(false);
        showStatus('Switched to Select.');
        return;
      }
      if (action === 'tool.character') {
        setTool('character');
        setSelection(null);
        setFocusDraftName(false);
        showStatus('Switched to Character. Type a name, choose a color, then click the map.');
        return;
      }
      if (action === 'tool.facing') {
        setTool('facing');
        setFocusDraftName(false);
        showStatus(selectedCharacter ? `Click the map to set facing for ${selectedCharacter.name}.` : 'Switched to Facing. Select a character first.');
        return;
      }
      if (action === 'tool.path') {
        setTool('path');
        setFocusDraftName(false);
        showStatus(selectedCharacter ? `Click the map to add a movement node for ${selectedCharacter.name}.` : 'Switched to Path. Select a character first.');
        return;
      }
      if (action === 'tool.camera') {
        setTool('camera');
        setFocusDraftName(false);
        showStatus('Switched to Camera. Click the map to place a new camera.');
        return;
      }
      if (action === 'file.load') {
        fileRef.current?.click();
        return;
      }
      if (action === 'edit.undo') {
        undo();
        return;
      }
      if (action === 'edit.redo') {
        redo();
        return;
      }
      if (action === 'edit.deleteSelected') {
        if (selection) deleteSelected();
        else showStatus('Select a character or camera before deleting.');
        return;
      }
      if (action === 'edit.clearPath') {
        if (selectedCharacter) clearSelectedPath();
        else showStatus('Select a character before clearing its path.');
        return;
      }
      if (action === 'export.png') {
        void handleExport();
        return;
      }
      if (action === 'copy.prompt') {
        void copyText(prompt, 'Copied Prompt.');
        return;
      }
      if (action === 'copy.json') {
        void copyText(JSON.stringify(map, null, 2), 'Copied JSON.');
        return;
      }
      if (action === 'view.cancel') {
        setSelection(null);
        setTool('select');
        setFocusDraftName(false);
        showStatus('Cleared selection and returned to Select.');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    dragging,
    future,
    map,
    past,
    prompt,
    recordingAction,
    selectedCamera,
    selectedCharacter,
    selection,
    shortcutSettingsOpen,
    shortcuts,
    tool
  ]);

  const selectedName = selectedCharacter?.name ?? selectedCamera?.label ?? 'No object selected';
  const isSupportPage = window.location.pathname.replace(/\/$/, '') === '/support';
  const shortcutLabel = (action: ShortcutAction) => formatShortcutCombo(shortcuts[action]?.[0] ?? 'Unset');

  if (isSupportPage) {
    return <SupportPage />;
  }

  return (
    <main className="app-shell" onDrop={handleDrop} onDragOver={(event) => event.preventDefault()}>
      <header className="topbar">
        <div className="brand-block">
          <div className="brand-kicker">AI video blocking tool</div>
          <h1>ShotMap Studio</h1>
        </div>
        <nav className="tool-strip" aria-label="Editing tools">
          <button className="toolbar-button" onClick={() => fileRef.current?.click()} title="Upload background">
            <Upload size={18} />
            <span>Upload</span>
            <ShortcutKey>{shortcutLabel('file.load')}</ShortcutKey>
          </button>
          <button
            className={tool === 'select' ? 'toolbar-button active' : 'toolbar-button'}
            onClick={() => {
              setTool('select');
              setFocusDraftName(false);
            }}
            title="Select and move"
          >
            <MousePointer2 size={18} />
            <span>Select</span>
            <ShortcutKey>{shortcutLabel('tool.select')}</ShortcutKey>
          </button>
          <button
            className={tool === 'character' ? 'toolbar-button active' : 'toolbar-button'}
            onClick={() => {
              setTool('character');
              setSelection(null);
              setFocusDraftName(true);
              showStatus('Type a name, choose a color, then click the map to place a new character.');
            }}
            title="Add character"
          >
            <LocateFixed size={18} />
            <span>Character</span>
            <ShortcutKey>{shortcutLabel('tool.character')}</ShortcutKey>
          </button>
          <button
            className={tool === 'facing' ? 'toolbar-button active' : 'toolbar-button'}
            onClick={() => {
              setTool('facing');
              setFocusDraftName(false);
            }}
            title="Set facing direction"
          >
            <PenLine size={18} />
            <span>Facing</span>
            <ShortcutKey>{shortcutLabel('tool.facing')}</ShortcutKey>
          </button>
          <button
            className={tool === 'path' ? 'toolbar-button active' : 'toolbar-button'}
            onClick={() => {
              setTool('path');
              setFocusDraftName(false);
            }}
            title="Add movement path"
          >
            <Route size={18} />
            <span>Path</span>
            <ShortcutKey>{shortcutLabel('tool.path')}</ShortcutKey>
          </button>
          <button
            className={tool === 'camera' ? 'toolbar-button active' : 'toolbar-button'}
            onClick={() => {
              setTool('camera');
              setFocusDraftName(false);
            }}
            title="Add camera"
          >
            <Camera size={18} />
            <span>Camera</span>
            <ShortcutKey>{shortcutLabel('tool.camera')}</ShortcutKey>
          </button>
          <button className="icon-button" disabled={!past.length} onClick={undo} title="Undo">
            <Undo2 size={18} />
          </button>
          <button className="icon-button" disabled={!future.length} onClick={redo} title="Redo">
            <Redo2 size={18} />
          </button>
          <button className="toolbar-button primary" onClick={() => void handleExport()} title="Export PNG">
            <Download size={18} />
            <span>PNG</span>
            <ShortcutKey>{shortcutLabel('export.png')}</ShortcutKey>
          </button>
          <button className="toolbar-button" onClick={() => setShortcutSettingsOpen(true)} title="Shortcut settings">
            <Keyboard size={18} />
            <span>Shortcuts</span>
          </button>
          <KoFiSupportButton className="toolbar-kofi-widget" />
        </nav>
        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          className="hidden-input"
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) void loadBackgroundFile(file);
            event.currentTarget.value = '';
          }}
        />
      </header>
      <div className="toast-viewport" aria-live="polite" aria-atomic="true">
        {toast && (
          <div key={toast.id} className="status-toast">
            <CheckCircle2 size={18} aria-hidden="true" />
            <span>{toast.message}</span>
          </div>
        )}
      </div>
      {shortcutSettingsOpen && (
        <ShortcutSettingsDialog
          shortcuts={shortcuts}
          recordingAction={recordingAction}
          conflictMessage={shortcutConflict}
          onClose={() => {
            setShortcutSettingsOpen(false);
            setRecordingAction(null);
            setShortcutConflict('');
          }}
          onStartRecording={startShortcutRecording}
          onResetAction={resetShortcutAction}
          onResetAll={resetAllShortcuts}
        />
      )}

      <section className="workspace">
        <aside className="left-rail">
          <div className="panel-title">
            <FolderOpen size={16} />
            <span>Shot</span>
          </div>
          <button className="shot-card active">
            <span className="shot-number">01</span>
            <span>
              <strong>{map.shotTitle || SAMPLE_SHOT_TITLE}</strong>
              <small>{map.characters.length} characters / {map.cameras.length} cameras</small>
            </span>
          </button>
          <div className="mini-section">
            <span className="section-label">Scene Intent</span>
            <p>{map.sceneIntent || SAMPLE_SHOT_INTENT}</p>
          </div>
          <div className="mini-section">
            <span className="section-label">Legend</span>
            <div className="legend-list" aria-label="Blocking map legend">
              <div className="legend-row">
                <span className="legend-dot" />
                <span>Dot marks the current character position.</span>
              </div>
              <div className="legend-row">
                <span className="legend-facing" />
                <span>Facing triangle sits against the character dot.</span>
              </div>
              <div className="legend-row">
                <span className="legend-arrow dashed" />
                <span>Dashed arrow marks the movement path and direction.</span>
              </div>
              <div className="legend-row">
                <span className="legend-camera" />
                <span>Amber camera marks the camera position and shooting direction.</span>
              </div>
            </div>
          </div>
          <div className="mini-section">
            <span className="section-label">Template</span>
            <button
              className="ghost-action"
              onClick={() => {
                commit(initialMap, `Restored the "${SAMPLE_SHOT_TITLE}" sample template.`);
                setSelection(null);
              }}
            >
              Load rain alley template
            </button>
          </div>
          <div className="mini-section">
            <span className="section-label">Tips</span>
            <p>Choose Character and click the map to place a mark. Select a character, then switch to Facing or Path and click the map to define direction or movement.</p>
          </div>
          <ShortcutHelpPanel groups={visibleShortcutGroups} onOpenSettings={() => setShortcutSettingsOpen(true)} />
        </aside>

        <section className="stage-column">
          <div className="stage-header">
            <div>
              <span className="eyebrow">Current Plate</span>
              <strong>{map.backgroundName}</strong>
            </div>
            <span className="status-line">{status}</span>
          </div>

          <div
            ref={stageRef}
            className={`stage tool-${tool}`}
            onClick={handleStageClick}
            onPointerMove={handlePointerMove}
            onPointerUp={finishDrag}
            onPointerLeave={finishDrag}
          >
            <img className="stage-image" src={map.background} alt="Scene background plate" draggable={false} />
            <svg className="annotation-layer" viewBox="0 0 100 100" preserveAspectRatio="none" aria-label="Blocking annotation layer">
              {map.characters.map((character) => {
                const points = [character, ...character.path];
                const pathData = points.map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`).join(' ');
                const arrowFrom = points[points.length - 2];
                const arrowTo = points[points.length - 1];
                return character.path.length ? (
                  <g key={`path-${character.id}`}>
                    <path
                      className="movement-path"
                      d={pathData}
                      fill="none"
                      stroke={character.color}
                      strokeWidth={0.62}
                      strokeDasharray="2.2 1.45"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path className="arrow-tip" d={arrowTipPath(arrowFrom, arrowTo, 1.88, 1)} style={{ fill: character.color, stroke: character.color }} />
                  </g>
                ) : null;
              })}
              {map.cameras.map((camera) => {
                const end = vectorPoint(camera, camera.angle, 4.9);
                return (
                  <g key={camera.id} className="camera-vector">
                    <path className="camera-tip" d={arrowTipPath(camera, end, 1.85, 0.92)} style={{ fill: '#DDAA33', stroke: '#DDAA33' }} />
                  </g>
                );
              })}
            </svg>

            {map.cameras.map((camera) => (
              <button
                key={camera.id}
                className={selection?.kind === 'camera' && selection.id === camera.id ? 'camera-pin selected' : 'camera-pin'}
                style={{ left: `${camera.x}%`, top: `${camera.y}%` }}
                onPointerDown={(event) => startDrag(event, 'camera', camera.id)}
                onClick={(event) => event.stopPropagation()}
                title={camera.label}
              >
                <Aperture size={18} />
                <span>{camera.label}</span>
              </button>
            ))}

            {map.characters.map((character, index) => {
              const isSelected = selection?.kind === 'character' && selection.id === character.id;
              const placement = labelPlacementForCharacter(character, map);
              return (
                <button
                  key={character.id}
                  className={[
                    'character-pin',
                    character.facing !== undefined ? 'has-facing' : '',
                    isSelected ? 'selected' : '',
                    `label-${placement}`
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  style={
                    {
                      left: `${character.x}%`,
                      top: `${character.y}%`,
                      '--pin-color': character.color,
                      '--label-font-size': `${normalizeLabelSize(character.labelSize)}px`,
                      '--label-font-weight': normalizeLabelWeight(character.labelWeight),
                      '--facing-angle': `${normalizeAngle(character.facing ?? 0)}deg`
                    } as React.CSSProperties
                  }
                  onPointerDown={(event) => startDrag(event, 'character', character.id)}
                  onClick={(event) => event.stopPropagation()}
                  title={character.name}
                >
                  <span className="pin-label">{character.name}</span>
                  <span className="pin-dot">{index + 1}</span>
                </button>
              );
            })}
          </div>
        </section>

        <aside className="right-rail">
          {tool === 'character' && (
            <div className="panel-block">
              <div className="panel-title">
                <BringToFront size={16} />
                <span>New Character</span>
              </div>
              <label className="field-label">
                Name
                <input value={draftName} onChange={(event) => setDraftName(event.target.value)} placeholder="Example: Woman" autoFocus={focusDraftName} />
              </label>
              <div className="field-help">After creation, this name appears beside the character dot.</div>
              <div className="field-label">
                Color
                <CharacterColorPicker
                  value={draftColor}
                  recentColors={recentCharacterColors}
                  onChange={setDraftColor}
                  onCustomColor={rememberRecentCharacterColor}
                />
              </div>
              <div className="field-label">
                Name styling
                <CharacterLabelTypographyControls
                  size={draftLabelSize}
                  weight={draftLabelWeight}
                  onSizeChange={setDraftLabelSize}
                  onWeightChange={setDraftLabelWeight}
                />
              </div>
            </div>
          )}

          <div className="panel-block">
            <div className="panel-title">
              <LocateFixed size={16} />
              <span>Properties</span>
            </div>
            <div className="selected-name">{selectedName}</div>
            {selectedCharacter && (
              <div className="inspector-stack">
                <label className="field-label">
                  Character name
                  <input value={selectedCharacter.name} onChange={(event) => updateCharacter(selectedCharacter.id, { name: event.target.value })} />
                </label>
                <div className="field-label">
                  Character color
                  <CharacterColorPicker
                    value={selectedCharacter.color}
                    recentColors={recentCharacterColors}
                    onChange={updateSelectedCharacterColor}
                    onCustomColor={rememberRecentCharacterColor}
                  />
                </div>
                <div className="field-label">
                  Name styling
                  <CharacterLabelTypographyControls
                    size={normalizeLabelSize(selectedCharacter.labelSize)}
                    weight={normalizeLabelWeight(selectedCharacter.labelWeight)}
                    onSizeChange={(labelSize) => updateCharacter(selectedCharacter.id, { labelSize })}
                    onWeightChange={(labelWeight) => updateCharacter(selectedCharacter.id, { labelWeight })}
                  />
                </div>
                <label className="field-label">
                  Facing angle
                  <input
                    type="range"
                    min="0"
                    max="359"
                    value={Math.round(selectedCharacter.facing ?? 0)}
                    onChange={(event) => updateCharacter(selectedCharacter.id, { facing: Number(event.target.value) })}
                  />
                </label>
                <div className="two-actions">
                  <button className="ghost-action" onClick={clearSelectedPath}>Clear Path</button>
                  <button className="danger-action" onClick={deleteSelected}>
                    <Trash2 size={15} />
                    Delete
                  </button>
                </div>
              </div>
            )}
            {selectedCamera && (
              <div className="inspector-stack">
                <label className="field-label">
                  Camera name
                  <input value={selectedCamera.label} onChange={(event) => updateCamera(selectedCamera.id, { label: event.target.value })} />
                </label>
                <label className="field-label">
                  Shot type
                  <input value={selectedCamera.shotType} onChange={(event) => updateCamera(selectedCamera.id, { shotType: event.target.value })} />
                </label>
                <label className="field-label">
                  Camera movement
                  <input value={selectedCamera.movement} onChange={(event) => updateCamera(selectedCamera.id, { movement: event.target.value })} />
                </label>
                <label className="field-label">
                  Facing angle
                  <input
                    type="range"
                    min="0"
                    max="359"
                    value={Math.round(selectedCamera.angle)}
                    onChange={(event) => updateCamera(selectedCamera.id, { angle: Number(event.target.value) })}
                  />
                </label>
                <button className="danger-action" onClick={deleteSelected}>
                  <Trash2 size={15} />
                  Delete
                </button>
              </div>
            )}
            {!selection && <p className="empty-copy">Click a character or camera to edit its properties here.</p>}
          </div>

          <div className="panel-block export-panel">
            <div className="panel-title">
              <Clipboard size={16} />
              <span>Export</span>
            </div>
            <textarea readOnly value={prompt} />
            <div className="two-actions">
              <button className="ghost-action" onClick={() => void copyText(prompt, 'Copied Prompt.')}>
                <Clipboard size={15} />
                Prompt
              </button>
              <button className="ghost-action" onClick={() => void copyText(JSON.stringify(map, null, 2), 'Copied JSON.')}>
                <FileJson size={15} />
                JSON
              </button>
            </div>
          </div>
        </aside>
      </section>
    </main>
  );
}

export default App;
